import React from 'react'
import * as Yup from 'yup'
import { Form, Formik, FormikHelpers } from 'formik'
import { yup_password_validation } from '@pepper/utils/src/validation/validation'

import { Button } from '../../atoms'
import { TextField, PasswordField } from '../../molecules'

import styles from './_.module.css'

type Phases = 'details' | 'otp' | 'success'

const forgotPasswordSchema: {
  [P in Phases]: Yup.ObjectSchema
} = {
  details: Yup.object({
    email: Yup.string()
      .email('Enter valid email id')
      .required('Enter Email-Id!'),
  }),
  otp: Yup.object({
    otp: Yup.string().required('Enter OTP!'),
    password: yup_password_validation.required('Enter Password'),
    confirm_password: Yup.string().test(
      'match',
      'Passwords do not match',
      function (password) {
        return password === this.parent.password
      }
    ),
  }),
  success: Yup.object({}),
}

interface Props {
  link: React.ElementType
  phase: Phases
  onResendOtp: (values: FormType) => void
  onSubmit: (values: FormType, action: FormikHelpers<FormType>) => void
  showHeading?: boolean
  showSignUpLink?: boolean
}

const initialFormValue = {
  details: {
    email: '',
  },
  otp: {
    otp: '',
    confirm_password: '',
    password: '',
  },
}

export type FormType = {
  email: string
  otp: string
  password: string
  confirm_password: string
}

export const ForgotPassword: React.FC<Props> = ({
  link: Link,
  phase,
  onSubmit,
  onResendOtp,
  showHeading = true,
  showSignUpLink = true,
}) => {
  // const [errorMessage, setErrorMessage] = useState('')

  if (phase === 'success') {
    return (
      <main className={styles.successContainer}>
        {showHeading && (
          <>
            <h1 className={styles.heading}>Password Updated</h1>
            <p className="text-center">
              Your password was modified. <br />
              Please login with the new password.
            </p>
          </>
        )}
        <Button type="primary" size="large" block={true}>
          <Link to="/login">Go to Login</Link>
        </Button>
      </main>
    )
  }

  return (
    <main className={styles.container}>
      <Formik
        initialValues={initialFormValue[phase]}
        onSubmit={(values: any, actions) => onSubmit(values, actions)}
        validationSchema={forgotPasswordSchema[phase]}
      >
        {({ isValid, isSubmitting, handleBlur, touched, errors, values }) => (
          <Form className={styles.form}>
            {showHeading && (
              <>
                <h1 className={styles.heading}>
                  {phase === 'details'
                    ? 'Retrieve Your Account'
                    : 'Reset Your Password'}
                </h1>
                <div className={styles.infoMessages}>
                  {phase === 'details'
                    ? 'You will receive an OTP on your registered credentials'
                    : `Choose a new password for ${values.email}`}
                </div>
              </>
            )}

            {/* <div className={styles.errorMessages}>{errorMessage}</div> */}

            <div className={styles.formFields}>
              {phase === 'details' && (
                <TextField
                  name="email"
                  className={styles.formField}
                  onBlur={handleBlur}
                  error={touched.email && errors.email ? true : false}
                  fieldProps={{
                    placeholder: 'Email Address',
                    size: 'large',
                    autoComplete: 'off',
                  }}
                />
              )}

              {phase === 'otp' && (
                <React.Fragment>
                  <TextField
                    name="otp"
                    className={styles.formField}
                    onBlur={handleBlur}
                    error={touched.email && errors.email ? true : false}
                    fieldProps={{
                      placeholder: 'Enter OTP',
                      size: 'large',
                    }}
                  />
                  <PasswordField
                    name="password"
                    className={styles.formField}
                    onBlur={handleBlur}
                    error={touched.email && errors.email ? true : false}
                    fieldProps={{
                      placeholder: 'New Password',
                      size: 'large',
                    }}
                  />
                  <PasswordField
                    name="confirm_password"
                    className={styles.formField}
                    onBlur={handleBlur}
                    error={touched.email && errors.email ? true : false}
                    fieldProps={{
                      placeholder: 'Confirm New Password',
                      size: 'large',
                    }}
                  />
                  <p className={styles.resendOtpText}>
                    Did not receive the OTP?{' '}
                    <Button
                      type="link"
                      size="small"
                      onClick={() => onResendOtp(values)}
                    >
                      <span className="underline">Send Again</span>
                    </Button>
                  </p>
                </React.Fragment>
              )}

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                className={styles.cta}
                disabled={!isValid}
                loading={isSubmitting}
                block={true}
              >
                Submit
              </Button>
            </div>
            <div className={styles.otherLinks}>
              <Link to={`/login${window.location?.search}`}>
                Return to Login
              </Link>
              {showSignUpLink ? (
                <Link to={`/signup${window.location?.search}`}>
                  Don’t have an account?
                </Link>
              ) : (
                <div />
              )}
            </div>
          </Form>
        )}
      </Formik>
    </main>
  )
}
